import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { finalize, skip, takeUntil } from 'rxjs/operators';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { DateService } from '@app-core/services/date/date.service';
import { DataService } from '@app-core/services/data/data.service';
import { DriverStatsParams } from 'src/app/home/models/home.model';
import { HomeService } from 'src/app/home/services/home.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { RequestDownloadReportBody } from '@app-core/models/core.model';
import { HttpErrorResponse } from '@angular/common/http';
import { AsyncReportType } from '@app-core/constants/constants';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
interface CoachingSessionReportDownloadBody extends RequestDownloadReportBody {
  dateFormat?: string;
  unit?: string;
  coachingSessionId: number;
  language?: string;
}
@Component({
  selector: 'app-coaching-session-report',
  templateUrl: './coaching-session-report.component.html',
  styleUrls: ['./coaching-session-report.component.scss'],
})
export class CoachingSessionReportComponent implements OnInit, OnDestroy {
  public downloadReportLoader: boolean = false;
  public isError: boolean = false;
  public getCoachingSessiondetailsLoader: boolean = true;
  public getTripAggregateLoader: boolean = true;
  public coachingSessionId: number = 1;
  public coachingSessionDetails;
  public uniqueCoachedEvents = [];
  public showDetails: boolean = false;
  public showError: boolean = false;
  public tripAggregatedetails: any = {};
  public driverId = new FormControl('');
  public sessionListLoader: boolean = false;
  public coachingSessionList = [];
  public currentMetricUnit = null;
  public eventLists = {};
  public fleetId: string;
  public csvDownloadSuccess: boolean = false;
  public enableDownloadReport: boolean = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private selectedCoachingIndex: number = -1;
  private totalEvents: number = 0;
  private skippedEvents: number = 0;
  private coachedEvents: number = 0;
  private noOfDays: number = 180;
  private currentDateFormat: string;
  private currentLanguage: string;

  constructor(
    private location: Location,
    private reportsService: ReportsService,
    private dateService: DateService,
    private homeService: HomeService,
    private snackBarService: SnackBarService,
    public dataService: DataService,
    public translate: TranslateService,
    private asyncDownloadsService: AsyncDownloadsService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit() {
    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
      }
    });

    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.navigateBack();
    });
    this.dataService._currentLanguage.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentLanguage = value;
      }
    });

    this.dataService._currentDateFormat.subscribe((value: string) => {
      if (value) {
        this.currentDateFormat = value;
      }
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public navigateBack() {
    this.location.back();
  }

  public driverChanged(driverId: string = '') {
    this.mixpanelService.track('Coaching Session Driver Filter', {
      DriverId: driverId,
    });
    this.driverId.patchValue(driverId);
    this.enableDownloadReport = false;
    this.getListOfCoachingSession();
  }

  public downloadFleetSafetyReport() {
    this.csvDownloadSuccess = null;
    this.mixpanelService.track('Download Coaching Session Report', {
      'Fleet Id': this.fleetId,
      'Coaching Session Id': this.coachingSessionId,
    });
    this.downloadReportLoader = true;
    const body: CoachingSessionReportDownloadBody = {
      reportType: 'COACHING_SESSION',
      coachingSessionId: this.coachingSessionId,
      reportFormat: 'PDF',
      language: this.currentLanguage,
      unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      dateFormat: this.currentDateFormat,
    };
    this.asyncDownloadsService
      .requestDownloadReport(body)
      .pipe(
        finalize(() => {
          this.downloadReportLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.csvDownloadSuccess = true;
        },
        (err: HttpErrorResponse) => {
          this.csvDownloadSuccess = false;
          if (err?.status === 409) {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public navigateToDownloads() {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.coachingSession.key);
  }

  public coachingSessionChange(event) {
    this.coachingSessionId = event?.value || 0;
    this.enableDownloadReport = false;
    this.selectedCoachingIndex = this.coachingSessionList.findIndex((item) => item.coachingSessionId === this.coachingSessionId);
    this.getCoachingSessionDetails();
  }

  public getCoachingSessionDetails() {
    this.mixpanelService.track('Change Coaching Session Filter', {
      'Coaching Session Id': this.coachingSessionId,
    });
    this.enableDownloadReport = true;
    this.showDetails = true;
    this.showError = false;
    this.getCoachingSessiondetailsLoader = true;
    this.getTripAggregateLoader = true;
    this.totalEvents = 0;
    this.skippedEvents = 0;
    this.coachedEvents = 0;
    this.uniqueCoachedEvents = [];
    const params = {
      fleetId: this.fleetId,
    };
    this.reportsService
      .getCoachingSessionDetails(this.coachingSessionId, params)
      .pipe(
        finalize(() => {
          this.getCoachingSessiondetailsLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          if (res) {
            this.coachingSessionDetails = res;
            const { events, accolades = [] } = this.coachingSessionDetails || [];
            this.totalEvents = events?.length || 0;
            events.map((e) => {
              if (e.status === 'COACHED') {
                this.coachedEvents++;
              } else {
                this.skippedEvents++;
              }
            });

            var accoladelsWithMedal = [];
            if (accolades && accolades.length) {
              accoladelsWithMedal =
                accolades?.map((event) => ({
                  modalPath: this.getMedals(event.percentile),
                  ...event,
                })) || [];
            }

            this.coachingSessionDetails = {
              ...this.coachingSessionDetails,
              accolades: accoladelsWithMedal,
              eventsCount: {
                totalEvents: this.totalEvents,
                skippedEvents: this.skippedEvents,
                coachedEvents: this.coachedEvents,
              },
            };
            this.uniqueCoachedEvents = events
              .filter((e) => e.status === 'COACHED')
              .filter((obj, index, self) => index === self.findIndex((t) => t.eventType === obj.eventType))
              .map((obj) => obj.eventType);
            this.getDriverTripsAggregate();
          }
        },
        () => {
          this.getTripAggregateLoader = false;
          this.showError = true;
          this.showDetails = false;
          this.coachingSessionDetails = {};
        }
      );
  }

  private getListOfCoachingSession() {
    if (!this.driverId.value) {
      return;
    }

    const dateRange = this.dateService.getDateRangeInISO(this.noOfDays);
    const { from, to } = dateRange;

    this.sessionListLoader = true;
    const params = {
      skip: 0,
      limit: 500,
      startDate: from,
      endDate: to,
    };

    params['driverIds[]'] = this.driverId.value;

    this.reportsService
      .getCoachingSessionList(params)
      .pipe(
        finalize(() => {
          this.sessionListLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          if (res) {
            const previouslist = this.coachingSessionList;
            const isSameList =
              previouslist.length == res?.rows.length &&
              previouslist.every(function (element, index) {
                return element === res.rows[index];
              });
            if (isSameList) {
              this.enableDownloadReport = true;
            } else {
              this.enableDownloadReport = false;
            }
            const { rows = [] } = res || {};
            this.coachingSessionList = rows;
          }
        },
        () => {
          this.coachingSessionList = [];
        }
      );
  }

  private getDriverTripsAggregate() {
    const dateRange = this.dateService.getDateRangeInISO(this.noOfDays);
    var { from } = dateRange;
    if (this.selectedCoachingIndex != -1 && this.coachingSessionList[this.selectedCoachingIndex + 1]) {
      from = this.coachingSessionList[this.selectedCoachingIndex + 1]?.createdAt;
    }
    const driverStatsParams = new DriverStatsParams({
      fleetId: this.fleetId,
      driverId: this.driverId.value,
      startDate: from,
      endDate: this.coachingSessionList[this.selectedCoachingIndex]?.createdAt,
      includeEventDiff: true,
      unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
    });

    this.homeService
      .getDriverStats(driverStatsParams)
      .pipe(
        finalize(() => {
          this.getTripAggregateLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (result) => {
          const { aggregate: { eventCount = {} } = {} } = result;
          this.eventLists = eventCount;
        },
        () => {
          this.eventLists = {};
        }
      );
  }

  private getMedals(percentile: number) {
    if (percentile >= 1 && percentile <= 5) {
      return '/assets/common/medal1.png';
    } else if (percentile >= 5.1 && percentile <= 10) {
      return '/assets/common/medal2.png';
    } else if (percentile >= 10.1 && percentile <= 25) {
      return '/assets/common/medal3.png';
    }
  }

  public scheduleCoachingSession() {
    this.mixpanelService.track('Schedule Clicked- From Overview Tab -Coaching Session Report', {
      'Report Type': AsyncReportType.coachingSession.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.coachingSession.key);
  }
}
